import { apiService } from './api';

export interface WebsiteInvoiceData {
  invoice_id: string;
  invoice_number: number;
  invoice_name: string;
  invoice_date: string | null;
  due_date: string | null;
  payment_status: string;
  payment_date: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  currency: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  customer_name: string;
  customer_contact_name: string | null;
  customer_contact_role: string | null;
  customer_address_street: string | null;
  customer_address_postcode: string | null;
  customer_address_city: string | null;
  is_paid: boolean;
}

export interface WebsiteInvoiceResponse {
  success: boolean;
  data?: WebsiteInvoiceData;
  error?: string;
}

class WebsiteInvoiceService {
  async getInvoice(invoiceId: string): Promise<WebsiteInvoiceResponse> {
    try {
      const response = await apiService.get(`/api/v1/website/invoices/${invoiceId}`) as any;
      
      if (response?.success && response?.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.error_message || 'Invoice not found or not paid'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get invoice'
      };
    }
  }

  async getPublicInvoice(invoiceId: string): Promise<WebsiteInvoiceResponse> {
    try {
      const response = await apiService.get(`/api/v1/website/invoices/${invoiceId}/public`) as any;
      
      if (response?.success && response?.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.error_message || 'Invoice not found or not paid'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get public invoice'
      };
    }
  }

  async renderInvoiceHtml(invoiceId: string): Promise<string | null> {
    try {
      // For HTML content, we'll use the authenticated endpoint and let the browser handle it
      // In a real implementation, you might want to use a different approach
      return null;
    } catch (error: any) {
      console.error('Failed to render invoice HTML:', error);
      return null;
    }
  }

  async renderPublicInvoiceHtml(invoiceId: string): Promise<string | null> {
    try {
      // For HTML content, we'll use the public endpoint and let the browser handle it
      // In a real implementation, you might want to use a different approach
      return null;
    } catch (error: any) {
      console.error('Failed to render public invoice HTML:', error);
      return null;
    }
  }

  getInvoiceUrl(invoiceId: string): string {
    return `/api/v1/website/invoices/${invoiceId}/public/render`;
  }

  getPdfDownloadUrl(invoiceId: string, language: string = 'en'): string {
    return `/api/v1/website/invoices/${invoiceId}/public/pdf?language=${language}`;
  }

  getAuthenticatedPdfDownloadUrl(invoiceId: string, language: string = 'en'): string {
    return `/api/v1/website/invoices/${invoiceId}/pdf?language=${language}`;
  }

  async downloadPdf(invoiceId: string, language: string = 'en'): Promise<boolean> {
    try {
      const url = this.getPdfDownloadUrl(invoiceId, language);
      
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      console.error('Failed to download PDF:', error);
      return false;
    }
  }

  async downloadAuthenticatedPdf(invoiceId: string, language: string = 'en'): Promise<boolean> {
    try {
      const url = this.getAuthenticatedPdfDownloadUrl(invoiceId, language);
      
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      console.error('Failed to download authenticated PDF:', error);
      return false;
    }
  }
}

export const websiteInvoiceService = new WebsiteInvoiceService();
export default websiteInvoiceService;